import { subDays, startOfDay, endOfDay, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, getDay, startOfWeek, addDays } from "date-fns";

/**
 * Get start and end Date for a number of days back from today.
 * start = start of day (daysBack ago), end = end of today.
 */
export function getDateRangeForDaysBack(daysBack) {
  const end = endOfDay(new Date());
  const start = startOfDay(subDays(new Date(), daysBack));
  return { start, end };
}

/**
 * Firestore query range on **`completedOn`** for `reportExercise` when deriving "last N days" insights.
 *
 * Queries use Timestamp bounds on `completedOn` (see composite indexes). We load a wide window,
 * then filter client-side to {@link getDateRangeForDaysBack} so charts stay a true rolling window.
 *
 * @param {number} daysBack - display window (e.g. 90)
 * @returns {{ start: Date, end: Date }}
 */
export function getProgressInsightsFirestoreQueryRange(daysBack) {
  const end = endOfDay(new Date());
  const lookbackDays = Math.max(daysBack + 180, 730);
  const start = startOfDay(subDays(new Date(), lookbackDays));
  return { start, end };
}

/**
 * Get start and end Date for the month containing the given date.
 * @param {Date} date - any date in the target month
 * @returns {{ start: Date, end: Date }}
 */
export function getMonthRange(date) {
  const d = date instanceof Date ? date : new Date(date);
  return {
    start: startOfMonth(d),
    end: endOfMonth(d),
  };
}

/**
 * Firestore date range for loading reportExercise / reportHealth for a visible calendar month.
 *
 * `reportExercise` is queried by **`completedOn`** (Timestamp). That includes sessions that
 * started earlier but finished in this month without relying on `createdOn` (which may be a
 * string and not match Timestamp range queries). Month bounds are enough for `completedOn`.
 *
 * @param {Date} viewMonth - any date in the month being viewed
 * @returns {{ start: Date, end: Date }}
 */
export function getCalendarDataQueryRange(viewMonth) {
  const d = viewMonth instanceof Date ? viewMonth : new Date(viewMonth);
  return {
    start: startOfMonth(d),
    end: endOfMonth(d),
  };
}

/**
 * Build calendar grid for a month: array of { date: Date | null, dayNum: number | null }.
 * Leading nulls so first day aligns with weekday (0 = Sun).
 * @param {Date} viewMonth - first day of month or any date in that month
 * @returns {{ date: Date|null, dayNum: number|null }[]}
 */
export function getCalendarDaysForMonth(viewMonth) {
  const start = startOfMonth(viewMonth);
  const end = endOfMonth(viewMonth);
  const days = eachDayOfInterval({ start, end });
  const firstWeekday = getDay(start);
  const leadingNulls = Array(firstWeekday).fill(null).map(() => ({ date: null, dayNum: null }));
  const cells = leadingNulls.concat(
    days.map((date) => ({ date, dayNum: date.getDate() }))
  );
  return cells;
}

/**
 * One calendar week (Sunday-first, same as dashboard calendar headers).
 * @param {Date} anchorDate - any instant in that week
 * @param {{ weekStartsOn?: number }} [options]
 * @returns {{ date: Date, dayNum: number }[] }
 */
export function getCalendarDaysForWeek(anchorDate, { weekStartsOn = 0 } = {}) {
  const d = anchorDate instanceof Date ? anchorDate : new Date(anchorDate);
  const ws = startOfWeek(startOfDay(d), { weekStartsOn });
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(ws, i);
    return { date, dayNum: date.getDate() };
  });
}

export { addMonths, subMonths };
